import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'Economic Future: From Grind to Alignment',
  paragraphs: ["Today's economy rewards those who capture attention. Cognarchism rewards those who can manage complexity.", 'This is not just a political philosophy. It is an economic model built for an age of informational overload, where those who can remain clear-minded, disciplined, and strategically aligned create the greatest long-term value. The industrial age rewarded labor. The information age rewarded access. The next age rewards alignment.']
}, {
  number: '02',
  heading: 'From Random Opportunity to Precision Allocation',
  paragraphs: ['The legacy system forces people into a chaotic, low-resolution job market defined by misaligned careers, wasted talent, and years of drift. Cognarchism replaces luck with precision.', 'Through the NCJI framework, individuals are matched to high-leverage roles where their specific cognitive strengths generate the greatest value. An algorithmic Opportunity Map guides them toward roles that fit their demonstrated judgment profile, while continuous recalibration ensures they do not remain trapped below their true capacity.', 'The result is a system built to reduce wasted years, accelerate upward mobility, and maximize long-term earnings.']
}, {
  number: '03',
  heading: 'Judgment as the New Currency',
  paragraphs: ['In the old world, credentials are expensive signals that often fail to prove real capability. In Cognarchism, judgment becomes a measurable asset.', 'Your mind becomes your resume. A verified NCJI profile functions as a universal professional key, signaling reliability, emotional stability, and cognitive competence. Because these traits are already verified, individuals bypass much of the trust tax imposed by legacy systems: endless vetting, prolonged probation, and low-signal social maneuvering.', 'High-trust opportunities, leadership roles, and capital flow more naturally toward those with the clearest demonstrated signal.']
}, {
  number: '04',
  heading: 'The Accelerated Wealth Trajectory',
  paragraphs: ['Traditional careers often plateau because people optimize for comfort, fall into drift, or lack an architecture for structured advancement. Cognarchism breaks that ceiling through recurring upgrade cycles.', "As an individual's NCJI profile strengthens, access to higher-stakes decision environments expands with it. Earlier entry into high-trust roles leads to earlier access to capital, authority, equity, and strategic influence. The state does not merely employ talent; it invests in its continued development, ensuring that human capital compounds over time."]
}, {
  number: '05',
  heading: 'Access to High-Trust Networks',
  paragraphs: ['Economic outcomes are shaped by network quality as much as individual skill. Cognarchism creates a closed-loop ecosystem of verified, high-judgment actors.', 'Partnerships move faster because both sides operate from a foundation of demonstrated alignment. High-trust individuals are more likely to hire, fund, and collaborate with others who meet the same standard. The result is a parallel economy built on competence, reduced friction, and better decision quality.']
}, {
  number: '06',
  heading: 'Reduced Risk, Higher Return',
  paragraphs: ['Most catastrophic financial failures are psychological before they are mathematical. Cognarchism is designed to reduce those failures at their source.', 'By strengthening consequential thinking, individuals are trained to evaluate second- and third-order effects rather than react to hype, fear, or short-term emotional pressure. By strengthening resistance to manipulation, they become less vulnerable to bubbles, predatory narratives, and panic-driven mistakes.', 'The result is more consistent judgment, lower self-inflicted volatility, and greater long-term resilience.']
}, {
  number: '07',
  heading: 'Status That Converts to Opportunity',
  paragraphs: ['In legacy systems, status is often symbolic and performative. In Cognarchism, status is functional.', 'Higher tiers unlock real opportunities: strategic roles, investment access, institutional responsibility, and deeper civic trust. Status is no longer decorative. It becomes a direct reflection of competence, and that competence converts into tangible economic power.']
}, {
  number: '08',
  heading: 'Escape from the Noise Economy',
  paragraphs: ['The current economy rewards attention-seeking, manipulation, and short-term spectacle. Cognarchism moves individuals into a signal economy.', 'Instead of competing in crowded, low-quality arenas dominated by branding and politics, people are directed toward long-term value creation. Energy is no longer wasted on theater. It is concentrated on the logic of the task, the quality of execution, and the durability of results.']
}, {
  number: '09',
  heading: 'The Compounding Effect',
  paragraphs: ['The real advantage of alignment is that it compounds.', 'Over a 20-year horizon, the difference between an aligned actor and a captured mind becomes enormous. Better decisions compound. Better networks compound. Better roles compound. Small improvements in judgment, repeated consistently over time, produce a widening gap in wealth, influence, and life trajectory.']
}, {
  number: '10',
  heading: 'Core Economic Pillars',
  paragraphs: [],
  subsections: [{
    title: 'The Opportunity Map',
    desc: "Within the CJI platform, the NCJI score functions not as a verdict, but as a compatibility key. It identifies where an individual's cognitive strengths — such as consequential reasoning or systems thinking — can generate the greatest value, aligning people with roles where they can achieve both mastery and meaning."
  }, {
    title: 'Cognitive Capital',
    desc: 'NCJI-verified individuals represent lower-risk, higher-reliability actors. This allows the state to extend sovereign-backed credit, reducing dependence on predatory debt and enabling long-term innovation, entrepreneurship, and durable value creation.'
  }, {
    title: 'Systemic Stability',
    desc: 'By prioritizing emotional regulation in positions of financial and institutional leadership, the system reduces volatility driven by panic and greed. The result is a quieter economy: stable, predictable, and more conducive to long-range planning.'
  }]
}, {
  number: '11',
  heading: 'Cultural Future: Ending the Outrage Loop',
  paragraphs: ['Modern culture is increasingly shaped by emotional manipulation. Outrage is monetized, attention is fragmented, and identity is pulled into endless reaction.', 'Cognarchism replaces that environment with a culture of self-mastery.'],
  subsections: [{
    title: 'A New Status Hierarchy',
    desc: 'Influence shifts away from performative visibility and toward demonstrated cognitive integrity. Status is earned through metacognition, clarity, grounded reasoning, and disciplined conduct rather than noise, spectacle, or emotional volatility.'
  }, {
    title: 'The Return of Agency',
    desc: 'When attention is no longer constantly hijacked, individuals regain the ability to focus. This creates the conditions for a cultural renaissance centered on deep work, creativity, philosophy, craftsmanship, and intentional living.'
  }, {
    title: 'National Cohesion',
    desc: 'By strengthening resistance to propaganda and manipulation, the system reduces tribal fragmentation. Culture becomes less of a battlefield and more of a cooperative project aimed at civilizational development.'
  }]
}, {
  number: '12',
  heading: 'The Lifecycle of Benefit',
  paragraphs: ['At the deepest level, people want stability: for their families, their health, and their future. Cognarchism builds the institutional pipeline to protect those priorities.'],
  subsections: [{
    title: 'Leadership Readiness',
    desc: 'Critical roles in education, governance, and public safety are entrusted to individuals with verified cognitive competence. Trust is no longer assumed. It is demonstrated, maintained, and earned.'
  }, {
    title: 'The Recalibration Safety Net',
    desc: 'When individuals experience burnout, instability, or cognitive decline, the system does not discard them. It responds with structured support. The 90-day recalibration pathway treats mental strain as a solvable condition, combining guidance, training, and institutional backing to restore capacity.'
  }, {
    title: 'Human-Centric Technology',
    desc: 'All major systems are governed to enhance autonomy rather than exploit weakness. Technology becomes a tool for empowerment, not extraction.'
  }]
}, {
  number: '13',
  heading: 'The Civilizational Upgrade',
  paragraphs: ['The transition is simple but profound: from a society of reactive consumers to a society of autonomous, self-directed individuals.', 'In this model, the economy is more stable because leaders think in longer time horizons. Culture is elevated because individuals govern their own attention. The future is more resilient because institutions are designed to cultivate, rather than constrain, human potential.', 'Cognarchism is not just a governance model. It is an attempt to upgrade the human operating system itself, so that people are finally able to pursue what actually matters.']
}];
export function HumanCapabilityEngine() {
  return <article data-id="element-383">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-384">
        <div className="max-w-3xl mx-auto px-6" data-id="element-385">
          <FadeIn delay={0.1} direction="down" data-id="element-386">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-387">
              Resource — Economic & Cultural Model
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-388">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-4 text-balance" data-id="element-389">
              The Human Capability Engine
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-390">
            <div className="text-base md:text-lg text-cognarch-gray leading-relaxed max-w-2xl mb-12 space-y-4" data-id="element-391">
              <p data-id="element-392">
                To understand Cognarchism, it must be seen not as a restrictive
                filter, but as a high-fidelity engine for human flourishing.
              </p>
              <p data-id="element-393">
                In today's world, people feel trapped — caught between economic
                instability and cultural exhaustion. They want purpose, agency,
                and the ability to build a stable future, yet they are
                surrounded by a noise-saturated environment that rewards
                distraction over clarity.
              </p>
              <p data-id="element-394">
                The Cognarchist State responds by transforming government into a
                Human Capability Utility: an architecture designed to align
                individuals with their highest potential.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-395">
            <Link to="/transition" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-396">
              Read the Transition Act
            </Link>
            <Link to="/ncji" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-397">
              Explore the NCJI
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-398">
          <div className="max-w-3xl mx-auto px-6" data-id="element-399">
            <FadeIn data-id="element-400">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-401">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-402">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-403">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-404">
              <div className="space-y-6" data-id="element-405">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-406">
                    {paragraph}
                  </p>)}

                {section.subsections && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-407">
                    {section.subsections.map((sub, sIndex) => <div key={sIndex} className="flex gap-4" data-id="element-408">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-409">
                          ◆
                        </span>
                        <div data-id="element-410">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-411">
                            {sub.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-412">
                            {sub.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-413">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-414">
          <FadeIn data-id="element-415">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-416" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-6 text-balance" data-id="element-417">
              You can continue navigating a world of randomness, noise, and
              misalignment. Or you can operate within a system where judgment is
              recognized, clarity is rewarded, and opportunity is structured.
            </p>
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-418">
              This is not just a philosophy. It is a civilizational economic
              upgrade.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-419" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-420">
              Alignment over randomness. Signal over noise. Capability over
              drift.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}